/**
 * @module string
 * @description String manipulation utility functions.
 */
import { isArray, isPlainObject, camelCase, keys } from "lodash";

/**
 * Converts all object keys to camelcase recursively
 * @param obj - The object or array to process
 * @returns The processed object with camelCase keys
 */
export const camelizeObject = <T>(obj: T): T => {
  if (isArray(obj)) {
    return obj.map(v => camelizeObject(v)) as unknown as T;
  } else if (isPlainObject(obj)) {
    return keys(obj as Record<string, unknown>).reduce((result, key) => ({
      ...result,
      [camelCase(key)]: camelizeObject((obj as Record<string, unknown>)[key])
    }), {}) as T;
  }
  return obj;
};

/**
 * Gets a parameter name string from an action type or dispatcher
 * First trims everything on the left of a slash, then removes the 'set' string and returns the camelCase value
 * @param string - The action type or dispatcher string to process
 * @returns The processed parameter name
 */
export const getStoreProp = (string: string | undefined): string => {
  if (!string) return '';
  return string
    .replace(/(^.*\/)/, '')
    .replace('set', '')
    .split('.')
    .map(sub => camelCase(sub))
    .join('.');
};

/**
 * Pluralize a word based on count
 * @param count - The count to determine pluralization
 * @param singular - The singular form of the word
 * @param plural - The plural form of the word (defaults to singular + 's')
 * @returns The appropriate form of the word based on count
 */
export const pluralize = (
  count: number, 
  singular: string, 
  plural = `${singular}s`
): string => {
  return count === 1 ? singular : plural;
};
